import { Column, DrawerActions, Inline, Stack } from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { parseURITemplate } from '@superdispatch/uri';
import { useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import {
  LOAD_PAYMENT_TYPES,
  formatCarrierPaymentMethod,
} from 'shared/helpers/CarrierPaymentTypes';
import { formatCurrency } from 'shared/helpers/IntlHelpers';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import { useFlag } from 'shared/settings/FeatureToggles';
import { DottedLineRow } from 'shared/ui/DottedLine';
import { LoadSendInvoiceDTO } from '../data/LoadActionsDTO';
import { OrdersDTO } from '../data/LoadDTO';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import {
  LoadInvoicePreviewDialog,
  formatInvoicePreviewDate,
} from './LoadInvoicePreviewDialog';
import { LoadSendInvoiceRequestPayment } from './LoadSendInvoiceRequestPayment';
import { PermissionButton } from 'shared/modules/permission/core/PermissionButton';

type PaymentMethodProp = typeof LOAD_PAYMENT_TYPES | string | undefined | null;

interface LoadSendInvoiceDrawerActionsProps {
  submitDisabled?: boolean;
  orders?: Array<Partial<OrdersDTO>>;
  paymentMethod: PaymentMethodProp;
  isCreatedByBroker?: boolean;
}

export function LoadSendInvoiceDrawerActions({
  submitDisabled = false,
  orders,
  paymentMethod,
  isCreatedByBroker,
}: LoadSendInvoiceDrawerActionsProps) {
  const [modalState, setModalState] = useState<'preview'>();
  const isPreviewInvoiceEnabled = useFlag('ctms_preview_invoice');
  const { isSubmitting } = useFormikContext<LoadSendInvoiceDTO>();
  const { values } = useFormikContext<LoadSendInvoiceDTO>();
  const {
    invoice_date,
    invoice_id,
    factor_this_invoice: hasFactorThisInvoice,
    bol_template,
    is_carrier_requested_superpay: isCarrierRequestedSuperPay,
  } = values;
  const totalPrice = useMemo(() => {
    let total = 0;
    const isOrdersValid =
      orders && orders.length > 0 && orders.every((val) => Boolean(val));
    if (isOrdersValid) {
      total = orders.reduce(
        (current, so) => current + Number(so.invoice_price),
        0,
      );
    }
    return total;
  }, [orders]);

  const paymentMethodType = useMemo(() => {
    if (orders && orders.length > 1) {
      return 'Mixed';
    }
    return formatCarrierPaymentMethod(paymentMethod);
  }, [orders, paymentMethod]);

  const guids: string =
    orders?.map((order) => order.guid as string).join(',') ?? '';

  return (
    <DrawerActions>
      <LoadInvoicePreviewDialog
        open={modalState === 'preview'}
        onClose={() => {
          setModalState(undefined);
        }}
        params={{
          load_guids: guids,
          invoice_number: invoice_id ?? '',
          invoice_date: formatInvoicePreviewDate(invoice_date),
          bol_template: bol_template || 'default',
          factor_this_invoice: !!hasFactorThisInvoice,
          is_carrier_requested_superpay:
            isCarrierRequestedSuperPay ?? undefined,
        }}
      />

      <Box width="100%" paddingBottom="medium" paddingTop="medium">
        <Stack space="small">
          <Column width="fluid">
            <DottedLineRow title="Method" value={paymentMethodType} />
          </Column>
          <LoadSendInvoiceRequestPayment
            paymentMethod={paymentMethod}
            isCreatedByBroker={isCreatedByBroker}
          />
          <Column width="fluid">
            <DottedLineRow
              title="Total"
              boldValue={true}
              value={formatCurrency(totalPrice)}
            />
          </Column>

          <Inline>
            <PermissionButton
              pending={isSubmitting}
              disabled={submitDisabled}
              name="loads"
              permission="can_send_invoice"
              type="submit"
              variant="primary"
            >
              Send Invoice
            </PermissionButton>

            <Button
              disabled={!orders?.length}
              onClick={() => {
                if (!orders) {
                  return;
                }
                trackLoadsEvent({ name: 'Carrier Clicked Preview Invoice' });

                if (isPreviewInvoiceEnabled) {
                  setModalState('preview');
                  return;
                }

                openExternalURL(
                  parseURITemplate(
                    '/tms/loads/invoice-preview/?send_invoice=preview&guids={guids}&invoice_date={invoice_date}&invoice_id={invoice_id}&factor_this_invoice={factor_this_invoice}&bol_template={bol_template}&is_carrier_requested_superpay={is_carrier_requested_superpay}',
                    {
                      send_invoice: 'preview',
                      guids,
                      invoice_date,
                      invoice_id,
                      factor_this_invoice: hasFactorThisInvoice,
                      bol_template,
                      is_carrier_requested_superpay: isCarrierRequestedSuperPay,
                    },
                  ),
                  { target: '_blank' },
                );
              }}
              variant="neutral"
            >
              Preview
            </Button>
          </Inline>
        </Stack>
      </Box>
    </DrawerActions>
  );
}
